/**
 * ProfilePicture Component
 *
 * A reusable avatar component that displays a user's profile picture
 * or falls back to their initials if no photo is available.
 *
 * Sizes:
 * - sm: 32px - top bar, sidebar bottom
 * - md: 48px - deal source cards
 * - lg: 56px - network cards
 * - xl: 80px - profile page
 */

import React, { useEffect, useState } from 'react';
import { cn } from '../lib/utils';

interface ProfilePictureProps {
  /** URL of the profile picture */
  photoUrl?: string | null;
  /** User's first name (for initials fallback) */
  firstName?: string;
  /** User's last name (for initials fallback) */
  lastName?: string;
  /** Full name (alternative to firstName/lastName) */
  name?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Additional CSS classes */
  className?: string;
  /** Show online status indicator */
  showOnlineIndicator?: boolean;
}

const sizeConfig = {
  sm: { container: 'size-8', text: 'text-xs', indicator: 'size-2.5 ring-[1.5px]' },
  md: { container: 'size-12', text: 'text-base', indicator: 'size-3 ring-2' },
  lg: { container: 'size-14', text: 'text-lg', indicator: 'size-3.5 ring-2' },
  xl: { container: 'size-20', text: 'text-2xl', indicator: 'size-4 ring-2' },
};

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  photoUrl,
  firstName,
  lastName,
  name,
  size = 'md',
  className = '',
  showOnlineIndicator = false,
}) => {
  const [imageError, setImageError] = useState(false);

  // A new photo deserves a fresh attempt even if the previous one failed
  useEffect(() => setImageError(false), [photoUrl]);

  const displayName = (name || [firstName, lastName].filter(Boolean).join(' ')).trim();

  const getInitials = (): string => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    const source = name || firstName || lastName;
    if (source) {
      const parts = source.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return source.charAt(0).toUpperCase();
    }
    return '?';
  };

  const config = sizeConfig[size];
  const showImage = photoUrl && !imageError;

  return (
    <div className={cn('relative inline-block shrink-0', className)}>
      {showImage ? (
        <img
          src={photoUrl}
          alt={displayName || 'User avatar'}
          className={cn(config.container, 'rounded-full bg-gray-100 object-cover ring-1 ring-black/5')}
          onError={() => setImageError(true)}
        />
      ) : (
        <div
          title={displayName || undefined}
          className={cn(
            config.container,
            config.text,
            'flex select-none items-center justify-center rounded-full bg-gray-100 font-semibold tracking-tight text-gray-700 ring-1 ring-inset ring-gray-200'
          )}
        >
          {getInitials()}
        </div>
      )}

      {showOnlineIndicator && (
        <span
          aria-hidden
          className={cn('absolute bottom-0 right-0 rounded-full bg-emerald-500 ring-white', config.indicator)}
        />
      )}
    </div>
  );
};

export default ProfilePicture;
